import * as React from 'react';
import classNames from 'classnames';
import { ApolloQueryResult } from '@apollo/client/core/types';
//import { ViewerProfile } from '../../../../../../common/types/socket-types';
import { AllowedSidebarMap } from '../../../hooks';
import { AnalyticsEventSource, PAGETITLE, ScreenEventAnalytics, UNKNOWN_SCREEN } from '@atlassian/temp-analytics';
import { AppContent, AppPage, PageHeader } from '../../../application';
import { ErrorPanel } from '../../../error-page';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
//import CommerceNoteSearch from '../../../common/containers/commerce-note-search';
import {
  getActionsDefault,
  getBreadcrumbTrailDefault,
  getHeaderDefault,
  getIconDefault,
  getSubheaderDefault,
} from '../billing-doc-utils';
import BillingDocsBreadcrumbs from './billing-doc-breadcrumbs';
import BillingDocDetails from './billing-doc-details';
//import BillingDocMessage from './billing-doc-message';
import './billing-doc-page.scss';
import { SourceSystem } from '@atlassian/cssxp-typescommon';
import { BillingDocWithLineItems, Invoice } from '@atlassian/cssxp-types-billing';
import { useFeatureToggle } from '../../../hooks/src/use-feature-toggle';
import { AuditNotes, AuditTrailTabProps } from '../../../audit-notes/src/audit-notes';
import { PaymentRetry } from '../../../payment-retry/index';
import { ENABLE_INVOICE_VIEW_PAYMENT_RETRY } from '@atlassian/cssxp-utils-generic';
import { getAdaBaseURL } from '../../../security/src/user-permission';
import { BillingDocMessageRefund } from './billing-doc-message-refund';
import { CommerceNoteSearchRefunds } from '../../../../account-subscriptions/cloud-site/src/tabs/commerce-note-search/commerce-note-search';
interface Props {
  analyticsScreenSource?: AnalyticsEventSource;
  additionalDetailsContent?: React.ReactNode;
  getBreadcrumbTrail?: typeof getBreadcrumbTrailDefault;
  headerProps?: {
    getHeader?: typeof getHeaderDefault;
    getSubheader?: typeof getSubheaderDefault;
    getIcon?: typeof getIconDefault;
    getActions?: typeof getActionsDefault;
    viewers?: [];
  };
  billingDoc?: BillingDocWithLineItems;
  isFetchingBillingDoc: boolean;
  billingDocFetchError: Error | null;
  auditTrailProps?: AuditTrailTabProps;
  BillingDocLineItems?: React.ComponentType<BillingDocWithLineItems>;
  allowedSidebars?: AllowedSidebarMap;
  paymentRetry?: Invoice['dunningStatus'];
  approvalSection?: React.ReactNode;
  onComplete: () => Promise<ApolloQueryResult<unknown>>;
  appPageClass?: string;
  fromCassi?: boolean;
  adaBaseUrl?: string;
  fromRefunds?: boolean;
}
/**
 * Common Billing Document Page for Invoices, Quotes, Refunds
 */
export const BillingDocPage = ({
  billingDoc,
  isFetchingBillingDoc,
  billingDocFetchError,
  headerProps,
  auditTrailProps,
  analyticsScreenSource,
  additionalDetailsContent,
  BillingDocLineItems,
  getBreadcrumbTrail,
  allowedSidebars,
  paymentRetry,
  onComplete,
  approvalSection,
  appPageClass,
  fromCassi = false,
  adaBaseUrl,
  fromRefunds = false,
}: Props) => {
  //TODO: Need to find if we need this orgaization and if need being in another story
  /*
  const domain = billingDoc?.domain;
  React.useEffect(() => {
    if (domain) {
      dispatch(fetchBillingDocOrganization(domain));
    }
  }, [domain, dispatch]);*/
  const organization = undefined;

  const [isEnabled] = useFeatureToggle();

  if (isFetchingBillingDoc) {
    return <LoadingIndicator />;
  }

  if (billingDocFetchError) {
    return (
      <div data-testid="error-panel">
        <ErrorPanel error={billingDocFetchError} />
      </div>
    );
  }

  if (!billingDoc) {
    return null;
  }

  const renderHeader = () => {
    const getHeader = headerProps?.getHeader ?? getHeaderDefault;
    const getSubheader = headerProps?.getSubheader ?? getSubheaderDefault;
    const getIcon = headerProps?.getIcon ?? getIconDefault;
    const getActions = headerProps?.getActions ?? getActionsDefault;
    const adaBaseUrl = getAdaBaseURL();
    return (
      <PageHeader
        icon={getIcon(billingDoc)}
        header={getHeader(billingDoc)}
        subheader={getSubheader(billingDoc)}
        actions={getActions(billingDoc, onComplete, adaBaseUrl)}
        viewers={headerProps?.viewers}
      />
    );
  };

  const renderBreadcrumbs = () => {
    const breadcrumbTrail = (getBreadcrumbTrail ?? getBreadcrumbTrailDefault)(billingDoc, organization);
    return <BillingDocsBreadcrumbs breadcrumbTrail={breadcrumbTrail} />;
  };

  // eslint-disable-next-line no-unused-vars
  const renderAuditTrail = (_sourceSystem?: SourceSystem | null) => {
    if (auditTrailProps) {
      return <AuditNotes {...auditTrailProps} />;
    }
    return null;
  };
  const renderAuditTrailRefund = (_sourceSystem?: SourceSystem | null) => {
    if (auditTrailProps) {
      return (
        //@ts-ignore
        <CommerceNoteSearchRefunds {...auditTrailProps} sourceSystem={_sourceSystem} />
      );
    }
    return null;
  };
  const renderLineItems = (): JSX.Element | undefined => {
    if (BillingDocLineItems) {
      return <BillingDocLineItems {...billingDoc} />;
    }
  };

  const renderApprovalSection = () => {
    return <>{approvalSection}</>;
  };

  return (
    <AppPage className={classNames('billing-document', appPageClass)} allowedSidebars={allowedSidebars}>
      <ScreenEventAnalytics
        event={{
          name: analyticsScreenSource ?? UNKNOWN_SCREEN,
          attributes: {
            id: billingDoc.id,
            entityName:
              analyticsScreenSource === AnalyticsEventSource.INVOICE_SCREEN
                ? PAGETITLE.INVOICE
                : analyticsScreenSource === AnalyticsEventSource.REFUND_SCREEN
                ? PAGETITLE.CREDIT_NOTES
                : null,
          },
        }}
      />
      {renderBreadcrumbs()}
      <AppContent className="billing-details">
        <div>{renderHeader()}</div>
        {/* TODO: Need to change further if need HAMS related invoice */}
        {/*{billingDoc.sourceSystem === SourceSystem.HAMS && (
          <>
             Perhaps not pass audit trail, maybe create an entity enum
            <BillingDocMessage entity={'invoice'} adaUrl={auditTrailProps?.adaUrl} />
          </>
        )}*/}
        {fromCassi && billingDoc.sourceSystem === SourceSystem.HAMS && (
          <div id="billing-doc-message" data-testid="billing-doc-message">
            <div className="info-banner" style={{ marginBottom: '32px' }}>
              <strong style={{ marginRight: '4px' }}>INTERNAL USE ONLY.</strong>
              This page is not a legal document - please go to ADA to get a copy of the{' '}
              <a target="_blank" rel="noopener" href={auditTrailProps?.adaUrl}>
                official invoice{auditTrailProps?.entity} →
              </a>
            </div>
          </div>
        )}
        {fromRefunds && <BillingDocMessageRefund adaUrl={adaBaseUrl} entity={billingDoc.id} />}
        <BillingDocDetails additionalContent={additionalDetailsContent} billingDoc={billingDoc} />
        {renderLineItems()}
        {!fromRefunds && renderAuditTrail(billingDoc.sourceSystem)}
        {fromRefunds && renderAuditTrailRefund(billingDoc.sourceSystem)}
        {renderApprovalSection()}
        {paymentRetry && isEnabled(ENABLE_INVOICE_VIEW_PAYMENT_RETRY) && <PaymentRetry dunningInfo={paymentRetry} />}
      </AppContent>
    </AppPage>
  );
};

export default BillingDocPage;
